/**
 * Bill insights — consumption history, neighbourhood comparison, and a
 * conservation goal with savings tips, surfaced directly on the customer's
 * bill rather than gated behind a separate portal login. Reuses the exact
 * analytics endpoints and chart components already built for the internal
 * ops tool (the customer tabs on `SensorDetailPage`, see
 * `features/customer/tabs.tsx`) — same math, same components, now reaching
 * the audience that actually opens a bill.
 *
 * Why on the bill and not only in an app: OPower's Home Energy Reports put
 * the neighbour comparison on the report itself, not behind a portal login,
 * specifically because a bill/report reaches every customer while an app
 * only reaches whoever installs it. A large-scale randomised trial (Allcott
 * 2011, ~600k households) measured a real ~1.4-3.3% consumption reduction
 * from that placement alone.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { HorizontalBarChart } from "@/components/charts/HorizontalBarChart";
import { TimeSeriesBarChart } from "@/components/charts/TimeSeriesBarChart";
import { CardSkeleton, EmptyState } from "@/components/LoadingSkeleton";
import { Panel } from "@/components/Panel";
import { StatTile } from "@/components/StatTile";
import {
  useBudgetGoal,
  usePeerComparison,
  useSavings,
} from "@/features/customer/queries";
import { useSensorDetail } from "@/features/sensors/queries";
import { dailyToChartPoints } from "@/features/sensors/series";
import { formatPKR } from "@/lib/currency";
import { chartColors } from "@/lib/tokens";
import { thresholds } from "@/lib/thresholds";

function goalStatusColor(status: string): string {
  if (status === "behind") return chartColors.critical();
  if (status === "on_track") return chartColors.warn();
  return chartColors.ok();
}

function peerBars(
  you: number,
  p25: number,
  median: number,
  p75: number,
  t: (k: string) => string,
) {
  return [
    { label: t("customer.you"), value: you, color: chartColors.brand() },
    { label: t("customer.p25"), value: p25, color: chartColors.fgMuted(0.45) },
    { label: t("customer.median"), value: median, color: chartColors.fgMuted(0.65) },
    { label: t("customer.p75"), value: p75, color: chartColors.fgMuted(0.85) },
  ];
}

export function BillInsights({ sensorId }: { sensorId: string }) {
  const { t } = useTranslation();
  // The widest window the customer-facing analytics accept (`customer_max_days`)
  // — one consistent "recent history" across the whole customer surface.
  const windowDays = thresholds().customer_max_days;
  const detail = useSensorDetail(sensorId, windowDays);
  const peer = usePeerComparison(sensorId);
  const goal = useBudgetGoal(sensorId);
  const savings = useSavings(sensorId);

  const daily = useMemo(
    () =>
      dailyToChartPoints(
        detail.data?.daily_consumption ?? [],
        detail.data?.expected_daily_readings ?? 4,
      ),
    [detail.data],
  );

  return (
    <div className="mt-6 space-y-6">
      <div>
        <h2 className="text-sm font-semibold text-fg">{t("billing.usageInsights")}</h2>
        <p className="text-xs text-fg-subtle">{t("billing.usageInsightsHint")}</p>
      </div>

      <Panel title={t("sensors.dailyConsumption")} windowDays={windowDays}>
        {detail.isLoading ? (
          <CardSkeleton />
        ) : daily.length === 0 ? (
          <EmptyState />
        ) : (
          <TimeSeriesBarChart
            ariaLabel={t("sensors.dailyConsumption")}
            data={daily}
            xKey="label"
            height={220}
            bars={[
              {
                key: "consumption",
                name: t("sensors.consumption"),
                color: chartColors.brand(),
              },
            ]}
          />
        )}
      </Panel>

      <Panel title={t("customer.usageVsPeers")}>
        {peer.isLoading ? (
          <CardSkeleton />
        ) : !peer.data ? (
          <EmptyState />
        ) : (
          <div className="space-y-4">
            <HorizontalBarChart
              ariaLabel={t("customer.usageVsPeers")}
              height={172}
              valueFormat={(v) => `${v.toFixed(1)} ${t("units.m3")}`}
              data={peerBars(
                peer.data.consumption_m3,
                peer.data.cohort_p25_m3,
                peer.data.cohort_median_m3,
                peer.data.cohort_p75_m3,
                t,
              )}
            />
            <div className="grid grid-cols-2 gap-4">
              <StatTile
                label={t("customer.percentile")}
                value={`${peer.data.percentile}%`}
                dotColor={chartColors.brand()}
              />
              <StatTile
                label={t("customer.vsCohort")}
                value={t(`customer.verdict.${peer.data.verdict}`, {
                  defaultValue: peer.data.verdict,
                })}
                dotColor={
                  peer.data.verdict === "above"
                    ? chartColors.critical()
                    : chartColors.ok()
                }
              />
            </div>
          </div>
        )}
      </Panel>

      <Panel title={t("customer.conservationGoal")}>
        {goal.isLoading || !goal.data ? (
          <CardSkeleton />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <StatTile
                label={t("customer.goalTarget")}
                value={`${goal.data.target_m3} ${t("units.m3")}`}
                dotColor={chartColors.ok()}
              />
              <StatTile
                label={t("customer.projected")}
                value={`${goal.data.projected_m3} ${t("units.m3")}`}
                dotColor={chartColors.brand()}
              />
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-bg-subtle">
              <div
                className="h-full"
                style={{
                  width: `${Math.min(100, goal.data.progress_pct)}%`,
                  backgroundColor: goalStatusColor(goal.data.status),
                }}
              />
            </div>
            <p
              className="text-sm font-medium"
              style={{ color: goalStatusColor(goal.data.status) }}
            >
              {t(`customer.goalStatus.${goal.data.status}`, {
                defaultValue: goal.data.status,
              })}
            </p>
          </div>
        )}

        {savings.data && savings.data.recommendations.length > 0 && (
          <ul className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
            {savings.data.recommendations.map((r) => (
              <li
                key={r.label}
                className="flex items-center justify-between rounded border border-border px-3 py-2"
              >
                <span className="text-fg-muted">
                  {t(`customer.saving.${r.label}`, { defaultValue: r.label })} (−
                  {r.reduce_m3} {t("units.m3")})
                </span>
                <span className="font-medium text-status-ok">
                  {formatPKR(r.save_pkr)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
}

export default BillInsights;
